import { useCallback, useMemo } from "react";
import { isRateFieldPath } from "@platform/btx-rates";

import {
  useCellReviewStore,
  type CellReviewFlagType,
  type CellReviewIdentityRequest,
  type CellReviewResponseRecord,
  type CellReviewStoreResult,
} from "../../shared/cellReviews/useCellReviewStore";
import { cellReviewsQueryKey } from "../../shared/cellReviews/fetchCellReviews";

const CELL_REVIEWS_QUERY_TAG = "quotes-cell-reviews";

const parseFieldPath = (
  fieldPath: string,
): { benefitKey: string; network: string | undefined } => {
  // A rates path is a single coordinate that happens to contain the same
  // separator; splitting it would scatter the coordinate across two columns.
  if (isRateFieldPath(fieldPath)) {
    return { benefitKey: fieldPath, network: undefined };
  }

  const sepIndex = fieldPath.indexOf("::");
  if (sepIndex === -1) {
    return { benefitKey: fieldPath, network: undefined };
  }
  return {
    benefitKey: fieldPath.slice(0, sepIndex),
    network: fieldPath.slice(sepIndex + 2) || undefined,
  };
};

const fieldPathOf = (review: CellReviewResponseRecord): string =>
  review.network
    ? `${review.benefitKey}::${review.network}`
    : review.benefitKey;

const identityKeyOf = (review: CellReviewResponseRecord): string =>
  `${review.benefitPlanId}::${fieldPathOf(review)}`;

interface UseQuotesCellReviewsOptions {
  rfpId: string | undefined;
  coverageType: string | undefined;
  enabled?: boolean;
  onSaveError?: (error: Error) => void;
}

/**
 * Adapts the shared cell-review store to the Quotes/SBS grid. Loads every
 * cell review for the RFP/coverage in a single request and partitions them
 * client-side into the internal "needs_review" flag, the external-facing
 * "broker_highlight", and the AI-extraction "edited" notation. Mutations are
 * reviewType-scoped so the flags coexist on the same cell without clobbering
 * each other.
 */
export const useQuotesCellReviews = ({
  rfpId,
  coverageType,
  enabled = true,
  onSaveError,
}: UseQuotesCellReviewsOptions): CellReviewStoreResult => {
  const buildIdentity = useCallback(
    (
      planId: string,
      fieldPath: string,
      reviewType: CellReviewFlagType,
    ): CellReviewIdentityRequest => {
      const { benefitKey, network } = parseFieldPath(fieldPath);
      return {
        scope: { type: "proposalPlan", rfpId, proposalPlanId: planId },
        coverageType: coverageType ?? "",
        benefitPlanId: planId,
        benefitKey,
        network,
        reviewType,
      };
    },
    [rfpId, coverageType],
  );

  // This tab's own list is scoped to one coverageType, so it can't share the
  // drawer's unscoped "proposal_plan" cache entry the way Comps/Responses do.
  // Invalidate that entry explicitly so its comment count stays current.
  const invalidatesQueryKeys = useMemo(
    () => (rfpId ? [cellReviewsQueryKey("proposal_plan", rfpId)] : []),
    [rfpId],
  );

  return useCellReviewStore({
    rfpId,
    queryKey: [CELL_REVIEWS_QUERY_TAG, rfpId, coverageType],
    listUrl:
      rfpId && coverageType
        ? `/rfps/${encodeURIComponent(rfpId)}/cell-reviews?${new URLSearchParams(
            { scopeType: "proposal_plan", coverageType },
          ).toString()}`
        : undefined,
    mutateUrl:
      rfpId && coverageType
        ? `/rfps/${encodeURIComponent(rfpId)}/cell-reviews`
        : undefined,
    enabled,
    supportsEdited: true,
    invalidatesQueryKeys,
    identityKeyOf,
    buildIdentity,
    onSaveError,
  });
};
